import React from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {getVendorGatewayMeta} from '../../constants/vendorGatewayLogos'
import EnvironmentBadge from './EnvironmentBadge'

export type GatewayTransactionRow = {
  id: number
  factory_name?: string
  gateway_name?: string
  environment?: string | null
  transaction_type: string
  status: string
  settlement_status?: string | null
  amount?: number | null
  currency?: string | null
  order_id?: number | null
  transaction_id?: string | null
  created_at?: string
}

type Filters = {
  gateway: string
  environment: string
  status: string
  period: string
}

export default function GatewayTransactionsTable({
  rows,
  filters,
  onFiltersChange,
  isLoading,
}: {
  rows: GatewayTransactionRow[]
  filters: Filters
  onFiltersChange: (next: Filters) => void
  isLoading?: boolean
}) {
  const {t} = useTranslation()

  return (
    <Stack gap='12px'>
      <Stack direction={{xs: 'column', sm: 'row'}} gap='12px' flexWrap='wrap'>
        <FormControl size='small' sx={{minWidth: 140}}>
          <InputLabel>{t('payment.period') as string}</InputLabel>
          <Select
            label={t('payment.period') as string}
            value={filters.period}
            onChange={(e) => onFiltersChange({...filters, period: e.target.value})}
          >
            <MenuItem value='24h'>24h</MenuItem>
            <MenuItem value='7d'>7d</MenuItem>
            <MenuItem value='30d'>30d</MenuItem>
          </Select>
        </FormControl>
        <FormControl size='small' sx={{minWidth: 140}}>
          <InputLabel>{t('payment.environment') as string}</InputLabel>
          <Select
            label={t('payment.environment') as string}
            value={filters.environment}
            onChange={(e) => onFiltersChange({...filters, environment: e.target.value})}
          >
            <MenuItem value='all'>{t('payment.all') as string}</MenuItem>
            <MenuItem value='sandbox'>{t('payment.env_sandbox') as string}</MenuItem>
            <MenuItem value='live'>{t('payment.env_live') as string}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {isLoading ? (
        <Typography color='var(--bs-gray-600)'>{t('payment.tx_loading') as string}</Typography>
      ) : rows.length === 0 ? (
        <Box p='24px' border='1px dashed var(--bs-gray-300)' borderRadius='8px' textAlign='center'>
          <Typography color='var(--bs-gray-600)'>{t('payment.tx_empty') as string}</Typography>
        </Box>
      ) : (
        <Box sx={{overflowX: 'auto'}}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>{t('payment.gateway_label') as string}</TableCell>
                <TableCell>{t('payment.environment') as string}</TableCell>
                <TableCell>{t('payment.tx_type') as string}</TableCell>
                <TableCell>{t('payment.status_label') as string}</TableCell>
                <TableCell>{t('payment.amount') as string}</TableCell>
                <TableCell>{t('payment.order') as string}</TableCell>
                <TableCell>{t('payment.date') as string}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const factory = row.factory_name || row.gateway_name || '—'
                const meta = getVendorGatewayMeta(factory)
                return (
                  <TableRow key={row.id}>
                    <TableCell>{meta?.name || factory}</TableCell>
                    <TableCell>
                      <EnvironmentBadge
                        environment={(row.environment as 'sandbox' | 'live') || undefined}
                        size='sm'
                      />
                    </TableCell>
                    <TableCell>{row.transaction_type}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>
                      {row.amount != null ? `${row.amount} ${row.currency || ''}`.trim() : '—'}
                    </TableCell>
                    <TableCell>{row.order_id ?? '—'}</TableCell>
                    <TableCell>
                      {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  )
}
